import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { App } from '@capacitor/app';
import { DeviceProvider } from '../../core-modules/providers/device/device';
import { EnvironmentProvider } from '../../core-modules/providers/environment-variables/environment';
/*
  Generated class for the ColorPaletteProvider provider.

  See https://angular.io/docs/ts/latest/guide/dependency-injection.html
  for more info on providers and Angular DI.
*/
@Injectable()
export class CoreConfiguration
{
    //#region Constructor

    /**
     * Class constructor
     */
    constructor
        (
            private device: DeviceProvider,
            private environment: EnvironmentProvider
        )
    {
    }

    //#endregion


    //#region Methods

    /**
     * Initialize the configuration provider, 
     * return true if the configuration is correct, false otherwise
     */
    public async initializeConfiguration(): Promise<void>
    {
        let state = await this.getApplicationState();

        // get the current device's identifier
        let deviceIdentifier = await this.device.getDeviceIdentifier();

        // get the stored identifier
        let storedIdentifier = state.DeviceIdentifier;

        //TODO : remove after some time temporary changeover due to different device identifier generation
        // if the state does not contain an application version it means that this is the first release
        // since upgrading to Capacitor 3, then if this is the case short the whole routine and proceed
        if (!state.ApplicationVersion || state.ApplicationVersion !== this.environment.appVersion)
        {
            await this.saveApplicationState({
                LoginToken: state.LoginToken,
                LoginDate: state.LoginDate,
                BiometricsEnabled: state.BiometricsEnabled,
                DeviceIdentifier: deviceIdentifier,
                ApplicationVersion: this.environment.appVersion
            });
        }
        // check the device identifiers in case the application's settings
        // were moved from one device to another 
        // if stored identifer is the same as device identifer, update state to handle old storage migration.
        else if (!storedIdentifier || storedIdentifier == deviceIdentifier)
        {
            // new configuration
            // if there is no identifier available then assume it's a new device
            // set the current identifier and resume accordingly
            await this.saveApplicationState({
                LoginToken: state.LoginToken,
                LoginDate: state.LoginDate,
                BiometricsEnabled: state.BiometricsEnabled,
                DeviceIdentifier: deviceIdentifier,
                ApplicationVersion: this.environment.appVersion
            });
        }
        else if (storedIdentifier !== deviceIdentifier)
        {
            // if the stored GUID is different from the current one reset all app
            // settings as probably this is data transferred from older devices
            // configuration is incorrect
            await this.saveApplicationState({
                LoginToken: null,
                LoginDate: null,
                BiometricsEnabled: null,
                DeviceIdentifier: deviceIdentifier,
                ApplicationVersion: this.environment.appVersion
            });
        }
    }

    /**
     * Get the application state
     */
    public async getApplicationState(): Promise<ApplicationState>
    {
        // check if storage method contains data, if so return it
        var applicationState = await Preferences.get({ key: ConfigurationOption.CORE_APPLICATION_STATE });

        if (applicationState.value)
        {
            return JSON.parse(applicationState.value);
        }

        // construct the application state object
        var result: ApplicationState = {
            LoginToken: '',
            LoginDate: new Date(),
            BiometricsEnabled: false,
            DeviceIdentifier: '',
            ApplicationVersion: this.environment.appVersion
        };

        // and return it
        return result;
    }

    /**
     * Save application state
     * @param state instance of application state
     */
    public async saveApplicationState(state: ApplicationState)
    {
        await Preferences.set({ key: ConfigurationOption.CORE_APPLICATION_STATE, value: JSON.stringify(state) });
    }

    /**
     * Set the user has fingerprint auth enabled setting
     * @param value 
     */
    public async setUserHasBiometricsEnabled(value: boolean): Promise<any>
    {
        // get current state
        let state = await this.getApplicationState();
        state.BiometricsEnabled = value;
        //update BiometricsEnabled of state
        return this.saveApplicationState(state);
    }

    /**
     * Clear the configuration of the application
     */
    public clearConfiguration(): Promise<any>
    {
        return Preferences.set({ key: ConfigurationOption.CORE_APPLICATION_STATE, value: '' });
    }

    //#endregion
}

/**
 * Interface for the Application State
 */
export interface ApplicationState
{
    LoginToken: string;
    LoginDate: Date;
    BiometricsEnabled: boolean;
    DeviceIdentifier: string;
    ApplicationVersion: string;
}

/**
 * List of Core Configuration options
 */
export enum ConfigurationOption
{
    CORE_DEVICE_IDENTIFIER = "core::config_device_identifier",
    CORE_USER_BIOMETRICS = "core::config_has_biometrics",
    CORE_LOGIN_TOKEN = "core::auth_login_token",
    CORE_LAST_LOGIN = "core::auth_last_login",
    CORE_APPLICATION_STATE = "core::application_state"
}